/**
 * The Apollo block only sees GraphQL. Journey events, S3 uploads, and REST post over their own
 * transport, so anything not guarded by hand leaked. This refuses non-reads by default instead.
 *
 * GraphQL is exempt because the Athena and Zeus clients enforce mutation policy and own the
 * `allowWhileImpersonating` allowlist this layer cannot infer from a POST. A third party writing
 * from its own origin (the HelloSign iframe) never reaches this code, so those handoffs are refused
 * at the point the app gives up control.
 *
 * A guardrail, not a boundary: devtools can restore the originals. Only the server can enforce it.
 */
import { UserError } from 'platform/Error';
import { areMutationsBlocked, refuseWrite } from 'platform/readOnlyMutations';
import { WEB_API_URL } from 'shared/helpers/globals';

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const GRAPHQL_PATHS = ['/api/graphql', '/api/zeus', '/api/zeus/customer-support'];

const API_PATH_PREFIX = '/api/';

const requestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

/**
 * Matched on origin, not on the path alone. Datadog RUM posts to `/api/v2/rum` and Sentry to
 * `/api/<id>/envelope/`, so testing for `/api/` anywhere in the URL refused our own telemetry.
 */
const isOurApi = (parsed: URL): boolean => {
  if (!parsed.pathname.startsWith(API_PATH_PREFIX)) {
    return false;
  }
  if (parsed.origin === window.location.origin) {
    return true;
  }
  try {
    return parsed.origin === new URL(WEB_API_URL).origin;
  } catch {
    return false;
  }
};

/**
 * Scoped to what holds customer data rather than to a list of exemptions. Listing exemptions meant
 * every telemetry host, the Cosmos login POST, and the chat service each needed an entry, and
 * forgetting one broke sign-in rather than leaking a write. This way an unlisted caller is allowed,
 * so the failure is a missed write instead of a broken page.
 */
const isCustomerData = (url: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return false;
  }
  const ourApi = isOurApi(parsed);
  if (ourApi && GRAPHQL_PATHS.includes(parsed.pathname)) {
    return false;
  }
  // S3 takes the document upload directly, and its presigned URL comes from a query rather than a
  // mutation, so the Apollo block never sees the PUT.
  return ourApi || parsed.hostname.endsWith('amazonaws.com');
};

const isBlocked = (url: string, method: string): boolean => {
  if (!areMutationsBlocked()) {
    return false;
  }
  if (READ_METHODS.has(method.toUpperCase())) {
    return false;
  }
  return isCustomerData(url);
};

/**
 * Names the refused call for the banner's toast. A path is more use to an employee than a bare
 * method, and the query string is dropped because it can carry customer identifiers.
 */
const describe = (url: string, method: string): string => {
  try {
    return `${method.toUpperCase()} ${new URL(url, window.location.origin).pathname}`;
  } catch {
    return method.toUpperCase();
  }
};

let installed = false;

export const installReadOnlyNetworkGuard = (): void => {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = requestUrl(input);
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
    if (isBlocked(url, method)) {
      return Promise.reject(new UserError(refuseWrite(describe(url, method))));
    }
    return originalFetch(input, init);
  };

  // axios sends the S3 document uploads over XHR, so patching fetch alone would miss them. The
  // signature is spelled out because `open` is overloaded and `.call` otherwise picks the 2-arg form.
  const originalOpen: (
    this: XMLHttpRequest,
    method: string,
    url: string,
    isAsync?: boolean,
    username?: string | null,
    password?: string | null,
  ) => void = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    isAsync: boolean = true,
    username?: string | null,
    password?: string | null,
  ): void {
    const href = typeof url === 'string' ? url : url.href;
    if (isBlocked(href, method)) {
      throw new UserError(refuseWrite(describe(href, method)));
    }
    originalOpen.call(this, method, href, isAsync, username, password);
  };
};
